import React, { useEffect, useRef, useState } from 'react';
import { ChatService, PaginationRequest, ChatHistoryResponse, ChatFilterRequest } from '../../services/ChatService';
import ChatModal from '../ChatModal';

interface ChatListProps {
    onChatSelect?: (chat: ChatHistoryResponse) => void;
}

const glowBtn: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
    background: 'linear-gradient(120deg, #0b3d91, #1e68ff)', color: '#fff', border: 'none',
    borderRadius: '10px', padding: '10px 18px', fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 10px 26px rgba(14,52,161,0.35)', transition: 'transform 0.15s ease, box-shadow 0.15s ease',
};

const secondaryBtn: React.CSSProperties = {
    ...glowBtn,
    background: 'linear-gradient(120deg, #e7eefc, #dfe7ff)', color: '#0f172a',
    boxShadow: '0 8px 18px rgba(12,28,70,0.18)',
};

const inputClass = "px-3 py-2 border border-[#ddd] rounded text-sm focus:outline-none focus:border-[#4a90e2] placeholder-[#999] transition-colors";

export const ChatList = React.forwardRef(({ onChatSelect }: ChatListProps, ref) => {
    const [chats, setChats] = useState<ChatHistoryResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [campaignId, setCampaignId] = useState('');
    const [userId, setUserId] = useState('');
    const [selectedChat, setSelectedChat] = useState<ChatHistoryResponse | null>(null);
    const [pagination, setPagination] = useState<PaginationRequest>({
        page: 1, size: 10, sort_by: 'created_at', sort_order: 'DESC'
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const filters: ChatFilterRequest = {
                campaign_id: campaignId ? parseInt(campaignId) : undefined,
                user_id: userId ? parseInt(userId) : undefined,
                question: searchTerm
            };
            const response = await ChatService.getChats(pagination, filters);
            setChats(response.items);
        } catch (error) {
            console.error('Error fetching chats:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [pagination.page]);

    const handlePageChange = (newPage: number) => setPagination(prev => ({ ...prev, page: newPage }));

    const handleClear = () => {
        setSearchTerm(''); setCampaignId(''); setUserId('');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSearchClick = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchData();
    };

    React.useImperativeHandle(ref, () => ({ fetchData }));

    return (
        <div className="w-full p-5">
            {/* Filters */}
            <div className="flex flex-wrap gap-5 mb-5 p-5 bg-[#f5f5f5] rounded-lg shadow-sm">
                {[
                    { label: 'Buscar por mensagem:', value: searchTerm, onChange: setSearchTerm, placeholder: 'Digite o texto da mensagem...' },
                    { label: 'ID da Campanha:', value: campaignId, onChange: (v: string) => /^\d*$/.test(v) && setCampaignId(v), placeholder: 'Digite o ID da campanha...' },
                    { label: 'ID do Usuário:', value: userId, onChange: (v: string) => /^\d*$/.test(v) && setUserId(v), placeholder: 'Digite o ID do usuário...' },
                ].map(({ label, value, onChange, placeholder }) => (
                    <div key={label} className="flex-1 min-w-[200px] flex flex-col gap-2">
                        <label className="font-medium text-[#333] text-sm">{label}</label>
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            placeholder={placeholder}
                            className={inputClass}
                        />
                    </div>
                ))}
                <div className="flex gap-2.5 items-end mt-2">
                    <button style={secondaryBtn} onClick={handleClear}>Limpar</button>
                    <button style={glowBtn} onClick={handleSearchClick}>Buscar</button>
                </div>
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto bg-white rounded-lg shadow-sm">
                {loading ? (
                    <div className="text-center p-5 text-[#666]">Carregando...</div>
                ) : (
                    <>
                        <table className="w-full border-collapse">
                            <thead>
                                <tr>
                                    {['Campanha', 'Pergunta', 'Resposta', 'Id do usuário', 'Tokens utilizados', 'Qtd. Contextos', 'Criado às', 'Atualizado às'].map(h => (
                                        <th key={h} className="p-3 text-left border-b border-[#eee] bg-[#f5f5f5] font-medium text-[#333]">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {chats.map(chat => (
                                    <tr
                                        key={chat.id}
                                        className="cursor-pointer transition-colors hover:bg-[#f9f9f9]"
                                        onClick={() => { setSelectedChat(chat); onChatSelect?.(chat); }}
                                    >
                                        <td className="p-3 border-b border-[#eee]">{chat.campaign_id}</td>
                                        <td className="p-3 border-b border-[#eee]">{chat.question}</td>
                                        <td className="p-3 border-b border-[#eee]">
                                            <span title={chat.answer || ''}>
                                                {chat.answer ? (chat.answer.length > 20 ? `${chat.answer.substring(0, 20)}...` : chat.answer) : '-'}
                                            </span>
                                        </td>
                                        <td className="p-3 border-b border-[#eee]">{chat.user_id}</td>
                                        <td className="p-3 border-b border-[#eee]">{chat.token_usage}</td>
                                        <td className="p-3 border-b border-[#eee]">{chat.contexts?.length || 0}</td>
                                        <td className="p-3 border-b border-[#eee] whitespace-nowrap">{new Date(chat.created_at).toLocaleString('pt-BR')}</td>
                                        <td className="p-3 border-b border-[#eee] whitespace-nowrap">{new Date(chat.updated_at).toLocaleString('pt-BR')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination */}
                        <div className="flex justify-center items-center gap-2.5 mt-5 p-2.5">
                            <button
                                style={glowBtn}
                                onClick={() => handlePageChange(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Anterior
                            </button>
                            <span className="text-[#666]">Página {pagination.page}</span>
                            <button
                                style={glowBtn}
                                onClick={() => handlePageChange(pagination.page + 1)}
                                disabled={chats.length < pagination.size}
                                className="disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Próxima
                            </button>
                        </div>
                    </>
                )}
            </div>

            <ChatModal chat={selectedChat} onClose={() => setSelectedChat(null)} />
        </div>
    );
});